import React, { useState, useEffect } from 'react';
import { mrpApi, warehousesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  RefreshCw,
  Package
} from 'lucide-react';

const MRPPage = () => {
  const { isAdmin } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchRecommendations = async (warehouseId = null) => {
    try {
      setLoading(true);
      const params = {};
      if (warehouseId && warehouseId !== 'all') {
        params.warehouse_id = warehouseId;
      }
      const response = await mrpApi.getRecommendations(params);
      setRecommendations(response.data);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await warehousesApi.list();
      setWarehouses(response.data.data);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  useEffect(() => {
    fetchWarehouses();
    fetchRecommendations();
  }, []);

  const handleWarehouseChange = (value) => {
    setSelectedWarehouse(value);
    fetchRecommendations(value);
  };

  const getPriorityBadge = (priority) => {
    const config = {
      HIGH: { label: 'Alta', className: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle },
      MEDIUM: { label: 'Media', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Info },
      LOW: { label: 'Baja', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle }
    };
    return config[priority] || config.LOW;
  };

  const getMethodLabel = (method) => {
    const labels = {
      SIMPLE_MIN_MAX: 'Min/Max',
      AVERAGE_CONSUMPTION: 'Consumo Promedio'
    };
    return labels[method] || method;
  };

  const highPriorityCount = recommendations.filter(r => r.priority === 'HIGH').length;
  const mediumPriorityCount = recommendations.filter(r => r.priority === 'MEDIUM').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="mrp-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">MRP - Reabasto</h1>
          <p className="text-slate-500 mt-1">Recomendaciones de reabasto basadas en reglas de inventario</p>
        </div>
        <Button 
          variant="outline"
          onClick={() => fetchRecommendations(selectedWarehouse)}
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className={highPriorityCount > 0 ? 'border-red-200 bg-red-50/50' : ''}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className={`p-3 rounded-sm ${highPriorityCount > 0 ? 'bg-red-500' : 'bg-slate-200'}`}>
              <AlertTriangle className={`w-6 h-6 ${highPriorityCount > 0 ? 'text-white' : 'text-slate-400'}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">Prioridad Alta</p>
              <p className="text-2xl font-bold text-slate-900">{highPriorityCount}</p>
            </div>
          </CardContent>
        </Card>

        <Card className={mediumPriorityCount > 0 ? 'border-amber-200 bg-amber-50/50' : ''}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className={`p-3 rounded-sm ${mediumPriorityCount > 0 ? 'bg-amber-500' : 'bg-slate-200'}`}>
              <Info className={`w-6 h-6 ${mediumPriorityCount > 0 ? 'text-white' : 'text-slate-400'}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">Prioridad Media</p>
              <p className="text-2xl font-bold text-slate-900">{mediumPriorityCount}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-sm bg-blue-900">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600">Total Sugerencias</p>
              <p className="text-2xl font-bold text-slate-900">{recommendations.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-700">Filtrar por almacén:</span>
            <Select value={selectedWarehouse} onValueChange={handleWarehouseChange}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Todos los almacenes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los almacenes</SelectItem>
                {warehouses.map((wh) => (
                  <SelectItem key={wh.id} value={wh.id}>
                    {wh.code} - {wh.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Recomendaciones de Reabasto
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
            </div>
          ) : recommendations.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">¡Todo en orden!</h3>
              <p className="text-slate-500">No hay recomendaciones de reabasto en este momento</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recommendations.map((rec, idx) => {
                const priorityConfig = getPriorityBadge(rec.priority);
                const PriorityIcon = priorityConfig.icon;
                
                return (
                  <div 
                    key={idx}
                    className={`p-4 rounded-md border transition-all hover:shadow-md ${
                      rec.priority === 'HIGH' ? 'border-red-200 bg-red-50/30' :
                      rec.priority === 'MEDIUM' ? 'border-amber-200 bg-amber-50/30' :
                      'border-slate-200 bg-slate-50/30'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-sm ${
                          rec.priority === 'HIGH' ? 'bg-red-100' :
                          rec.priority === 'MEDIUM' ? 'bg-amber-100' :
                          'bg-blue-100'
                        }`}>
                          <Package className={`w-5 h-5 ${
                            rec.priority === 'HIGH' ? 'text-red-600' :
                            rec.priority === 'MEDIUM' ? 'text-amber-600' :
                            'text-blue-600'
                          }`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-slate-900">{rec.product_name}</h4>
                            <Badge className={priorityConfig.className}>
                              <PriorityIcon className="w-3 h-3 mr-1" />
                              {priorityConfig.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 font-mono">{rec.product_sku}</p>
                          <p className="text-sm text-slate-600 mt-1">
                            {rec.warehouse_name || 'Global'} • Método: {getMethodLabel(rec.method)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 lg:text-right">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Stock Actual</p>
                          <p className={`text-xl font-bold tabular-nums ${
                            rec.current_stock <= 0 ? 'text-red-600' : 'text-slate-900'
                          }`}>
                            {rec.current_stock}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Sugerido</p>
                          <p className="text-xl font-bold text-blue-900 tabular-nums">
                            +{rec.suggested_quantity.toFixed(0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">Fecha Sugerida</p>
                          <p className="text-sm font-medium text-slate-700">
                            {new Date(rec.suggested_date).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-sm text-slate-600">{rec.explanation}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MRPPage;
